import { Camera } from 'types/camera';
import { Stream, StreamSource, StreamType } from 'types/stream';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQueries } from 'hooks/api/useAuthenticatedQuery';

type UseStreamsByCamerasOptions = {
  cameras: Camera[];
  sources?: StreamSource[];
  stream_types?: StreamType[];
  limit?: number;
  enabled?: boolean;
};

/**
 * Fetches streams for multiple cameras individually to ensure no streams are missed.
 * This is important because if one camera has many streams, a single batched query
 * could return all streams for that camera and miss streams for other cameras.
 */
export function useStreamsByCameras({
  cameras,
  sources = ['camera_stream', 'uri_stream', 'pipeline_stream'],
  stream_types = ['webrtc', 'rtsp'],
  limit = 50,
  enabled = true,
}: UseStreamsByCamerasOptions) {
  const { applicationID, client } = useAPI();

  // Fetch streams individually for each camera
  const streamQueries = useAuthenticatedQueries(
    cameras.map((camera) => ({
      queryKey: ['streams', applicationID, camera.id, { sources, stream_types, limit }],
      queryFn: async () => {
        const { data } = await client.get(
          `/internal/apps/${applicationID}/streams`,
          {
            params: {
              pagination: 'offset',
              page: 1,
              limit,
              sources,
              camera_ids: [camera.id],
              stream_types,
            },
          }
        );
        return data;
      },
      enabled,
    }))
  );

  // Flatten all stream results into a single array
  const streams: Stream[] = streamQueries
    .map((query) => (query.data as any)?.data || [])
    .flat();

  const isLoading = streamQueries.some((query) => query.isLoading);
  const isError = streamQueries.some((query) => query.isError);
  const isFetching = streamQueries.some((query) => query.isFetching);

  return {
    streams,
    isLoading,
    isError,
    isFetching,
    queries: streamQueries,
  };
}
